import { keyBy } from "lodash";
import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useImmer } from "use-immer";

import { exists, filterMatches } from "@examspark/common";
import { Loading, Toolbar } from "@examspark/common-ui";
import { ParsedObjectivesView } from "@examspark/types";

import { useQuerySearchBonfireObjectives } from "../../hooks/useQueries";

import AlignmentView from "./AlignmentView";
import { AlignmentViewData } from "./AlignmentViewData";
import VerticalAlignmentObjective from "./AlignmentViewObjective";
import { VerticalAlignmentSettings } from "./VerticalAlignmentSettings";

interface Props {
  data: Extract<ParsedObjectivesView, { type: "VERTICAL_ALIGNMENT" }>;
}

const VerticalAlignment = ({ data }: Props) => {
  const [showAllNotes, setShowAllNotes] = useState(false);
  const [showRowNotes, setShowRowNotes] = useImmer<Record<string, boolean>>({});
  const [searchParams] = useSearchParams();

  const [showAnnotationCcrs, setShowAnnotationCcrs] = useState(false);
  const [showAnnotationRcfp, setShowAnnotationRcfp] = useState(false);

  const initialObjectiveScope = searchParams.get("objectiveScope");

  const initialColumns = useMemo(() => {
    let initialColumn =
      data.data?.columns?.findIndex(
        (column) => column.objectiveScope === initialObjectiveScope,
      ) ?? -1;

    if (initialColumn === -1) {
      initialColumn = 1;
    }

    return [initialColumn - 1, initialColumn, initialColumn + 1];
  }, [data.data?.columns, initialObjectiveScope]);

  const allScopes = useMemo(
    () => data.data?.columns?.map((c) => c.objectiveScope).filter(exists),
    [data.data?.columns],
  );
  const objectivesQuery = useQuerySearchBonfireObjectives(allScopes);
  const objectives = useMemo(
    () => keyBy(objectivesQuery.data, (o) => o.id),
    [objectivesQuery.data],
  );

  const isSingleSection =
    (data.data?.sections?.length ?? 0) <= 1 &&
    data.data?.sections?.[0].title == null;

  const view = useMemo(() => {
    if (data.data === undefined) return undefined;

    const result: AlignmentViewData = {
      columns:
        data.data.columns?.map((c) => ({
          label: c.text,
          value: c.objectiveScope ?? c.text,
        })) ?? [],
      sections: data.data.sections?.map((s, i) => {
        const title = s.title ?? `Section ${i + 1}`;
        return {
          id: s.id,
          header: isSingleSection ? null : title,
          title,
          rows: s.rows?.map((r) => ({
            id: r.rowId,
            cells: r.columns?.map((c) => {
              const objective =
                c?.objectiveId == null ? null : objectives[c.objectiveId];

              return {
                contents: (objective != null || c?.text != null) && (
                  <VerticalAlignmentObjective
                    objective={objective}
                    text={c?.text}
                    showAllNotes={showAllNotes}
                    showNotes={showRowNotes[r.rowId] ?? false}
                    showAnnotations={{
                      "tcmpc.notes": true,
                      "tcmpc.ccrs": showAnnotationCcrs,
                      "tcmpc.rcfp": showAnnotationRcfp,
                    }}
                    onShowNotesChange={(value) =>
                      setShowRowNotes((draft) => {
                        draft[r.rowId] = value;
                      })
                    }
                  />
                ),
                matchesFilter: (filterText) =>
                  filterMatches(
                    filterText,
                    objective?.abbreviation,
                    objective?.content,
                    c?.text,
                  ),
              };
            }),
          })),
        };
      }),
    };

    return result;
  }, [
    data.data,
    isSingleSection,
    objectives,
    setShowRowNotes,
    showAllNotes,
    showAnnotationCcrs,
    showAnnotationRcfp,
    showRowNotes,
  ]);

  if (objectivesQuery.isLoading) {
    return <Loading>Loading Standards</Loading>;
  }

  if (view == null) return null;

  return (
    <AlignmentView
      data={view}
      initialColumns={initialColumns}
      hideNavigation={isSingleSection}
      toolbar={
        <Toolbar>
          <VerticalAlignmentSettings
            showAllNotes={showAllNotes}
            setShowAllNotes={setShowAllNotes}
            showAnnotationCcrs={showAnnotationCcrs}
            onShowAnnotationCcrsChange={setShowAnnotationCcrs}
            showAnnotationRcfp={showAnnotationRcfp}
            onShowAnnotationRcfpChange={setShowAnnotationRcfp}
          />
        </Toolbar>
      }
    />
  );
};

export default VerticalAlignment;
